import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

const ExportModal = ({ onClose, userEmail }) => {
  const [exportData, setExportData] = useState({
    email: userEmail || '',
    includeWorkEntries: true,
    includeAppointments: true,
    dateFilterType: 'all', // 'all', 'range', 'month'
    fromDate: '',
    toDate: '',
    selectedMonth: '',
    selectedYear: new Date().getFullYear()
  });
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  const handleExport = async () => {
    setError('');
    setSuccess('');

    if (!exportData.email) {
      setError('Please enter an email address');
      return;
    }

    if (!exportData.includeWorkEntries && !exportData.includeAppointments) {
      setError('Please select at least one data type to export');
      return;
    }

    if (exportData.dateFilterType === 'range' && (!exportData.fromDate || !exportData.toDate)) {
      setError('Please select both from and to dates');
      return;
    }

    if (exportData.dateFilterType === 'range' && exportData.fromDate > exportData.toDate) {
      setError('From date cannot be after to date');
      return;
    }

    if (exportData.dateFilterType === 'month' && !exportData.selectedMonth) {
      setError('Please select a month');
      return;
    }

    setExporting(true);

    try {
      const token = localStorage.getItem('token');
      let fromDate = null;
      let toDate = null;

      if (exportData.dateFilterType === 'range') {
        fromDate = exportData.fromDate;
        toDate = exportData.toDate;
      } else if (exportData.dateFilterType === 'month') {
        // Calculate first and last day of the month
        const year = exportData.selectedYear;
        const month = parseInt(exportData.selectedMonth);
        fromDate = `${year}-${month.toString().padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        toDate = `${year}-${month.toString().padStart(2, '0')}-${lastDay}`;
      }

      const payload = {
        email: exportData.email,
        include_work_entries: exportData.includeWorkEntries,
        include_appointments: exportData.includeAppointments,
        from_date: fromDate,
        to_date: toDate
      };

      const response = await fetch(`${backendUrl}/api/export/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSuccess(`Export sent successfully to ${exportData.email}!`);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to export data');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      setError('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-black border-4 border-[var(--accent)] w-full max-w-2xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-[var(--accent)]">[EXPORT DATA]</h2>
            <button onClick={onClose} className="text-[var(--accent)] hover:text-white">
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 border-2 border-red-500 bg-red-500/10 text-red-500">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 border-2 border-green-500 bg-green-500/10 text-green-500">
              {success}
            </div>
          )}

          <div className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-[var(--accent)] mb-2 font-bold">Email Address *</label>
              <input
                type="email"
                value={exportData.email}
                onChange={(e) => setExportData({ ...exportData, email: e.target.value })}
                className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                placeholder="Enter email address"
              />
            </div>

            {/* Data Selection */}
            <div>
              <label className="block text-[var(--accent)] mb-3 font-bold">What to Export *</label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exportData.includeWorkEntries}
                    onChange={(e) => setExportData({ ...exportData, includeWorkEntries: e.target.checked })}
                    className="w-5 h-5 bg-black border-2 border-[var(--accent)]"
                  />
                  <span className="text-[var(--accent)]">Work Entries (Time Tracking)</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exportData.includeAppointments}
                    onChange={(e) => setExportData({ ...exportData, includeAppointments: e.target.checked })}
                    className="w-5 h-5 bg-black border-2 border-[var(--accent)]"
                  />
                  <span className="text-[var(--accent)]">Job Appointments</span>
                </label>
              </div>
            </div>

            {/* Date Filter Options */}
            <div>
              <label className="block text-[var(--accent)] mb-3 font-bold">Date Filter</label>
              <div className="space-y-3">
                {/* All Data */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="dateFilter"
                    checked={exportData.dateFilterType === 'all'}
                    onChange={() => setExportData({ ...exportData, dateFilterType: 'all' })}
                    className="w-5 h-5"
                  />
                  <span className="text-[var(--accent)]">All Data (No Date Filter)</span>
                </label>

                {/* Date Range */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="dateFilter"
                      checked={exportData.dateFilterType === 'range'}
                      onChange={() => setExportData({ ...exportData, dateFilterType: 'range' })}
                      className="w-5 h-5"
                    />
                    <span className="text-[var(--accent)]">Date Range</span>
                  </label>
                  {exportData.dateFilterType === 'range' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-8 mt-2">
                      <div>
                        <label className="block text-[var(--accent)] mb-2 text-sm">From Date</label>
                        <input
                          type="date"
                          value={exportData.fromDate}
                          onChange={(e) => setExportData({ ...exportData, fromDate: e.target.value })}
                          className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                        />
                      </div>
                      <div>
                        <label className="block text-[var(--accent)] mb-2 text-sm">To Date</label>
                        <input
                          type="date"
                          value={exportData.toDate}
                          onChange={(e) => setExportData({ ...exportData, toDate: e.target.value })}
                          className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Month Selection */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="dateFilter"
                      checked={exportData.dateFilterType === 'month'}
                      onChange={() => setExportData({ ...exportData, dateFilterType: 'month' })}
                      className="w-5 h-5"
                    />
                    <span className="text-[var(--accent)]">Specific Month</span>
                  </label>
                  {exportData.dateFilterType === 'month' && (
                    <div className="grid grid-cols-2 gap-4 ml-8 mt-2">
                      <div>
                        <label className="block text-[var(--accent)] mb-2 text-sm">Month</label>
                        <select
                          value={exportData.selectedMonth}
                          onChange={(e) => setExportData({ ...exportData, selectedMonth: e.target.value })}
                          className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                        >
                          <option value="">-- Select Month --</option>
                          <option value="1">January</option>
                          <option value="2">February</option>
                          <option value="3">March</option>
                          <option value="4">April</option>
                          <option value="5">May</option>
                          <option value="6">June</option>
                          <option value="7">July</option>
                          <option value="8">August</option>
                          <option value="9">September</option>
                          <option value="10">October</option>
                          <option value="11">November</option>
                          <option value="12">December</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[var(--accent)] mb-2 text-sm">Year</label>
                        <select
                          value={exportData.selectedYear}
                          onChange={(e) => setExportData({ ...exportData, selectedYear: parseInt(e.target.value) })}
                          className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                        >
                          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                onClick={handleExport}
                disabled={exporting}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold"
              >
                {exporting ? 'EXPORTING...' : '[EXPORT & EMAIL]'}
              </Button>
              <Button
                onClick={onClose}
                disabled={exporting}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold"
              >
                [CANCEL]
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
